import api from './api';
import { normalizeCollection, type CollectionResponse } from './hydraCollection';

/** Primär- oder Sekundärbrennstoff (D-01) — unterschieden über dieses Feld, eine Tabelle für beide. */
export type FuelAshCategoryType = 'primary' | 'secondary';

/**
 * Aschegehalt-Stammdaten (B-04), wie sie `/api/fuel_ash_categories` liefert.
 *
 * `startValue`/`minValue`/`maxValue` sind DECIMAL(5,2)-Strings und dürfen fehlen (D-03) — ein
 * fehlender Schlüssel bedeutet leer, nicht 0. `nameLocked` ist seit Plan 34-05 belegt, sobald die
 * Verwendungsprüfung greift.
 *
 * Seit Plan 34-05 zwei unterschiedlich weite Antworten hinter derselben Ressource: Melder
 * (`getSecondaryForForm()`) sehen nur `id`/`type`/`name`/`startValue`/`catchAll` — `code`,
 * `minValue`, `maxValue`, `binding` und `nameLocked` sind Verwaltungsfelder und laufen über eine
 * feldscharfe `ApiProperty`-Sicherheit hinter `ROLE_ADMIN` (`FuelAshCategory.php`); bei einem
 * Melder fehlt der jeweilige JSON-Schlüssel ganz. `code`/`minValue`/`maxValue`/`nameLocked` sind
 * deshalb bereits optional typisiert — `binding` bewusst NICHT (siehe `getSecondaryForForm()`
 * unten): die admin-only Einstellungsseite (`fuelAshForm.ts`) verlangt dort einen echten
 * `boolean`, und dieser Typ wird von beiden Antwortformen geteilt. Für einen Melder ist
 * `category.binding` zur Laufzeit `undefined`, obwohl der Typ `boolean` behauptet — im
 * Meldeformular wird das Feld nirgends gelesen (D-17: der Aschegehalt-Startwert kommt aus
 * `startValue`, nicht aus `binding`/`minValue`/`maxValue`).
 */
export interface FuelAshCategory {
  id: number;
  type: FuelAshCategoryType;
  code?: string | null;
  name: string;
  startValue?: string | null;
  minValue?: string | null;
  maxValue?: string | null;
  binding: boolean;
  catchAll: boolean;
  /** D-10: gesetzt, sobald die Kategorie in einer Meldung verwendet wurde. Admin-only Feld (siehe
   *  Docblock oben) — im Meldeformular (`getSecondaryForForm()`) immer `undefined`, und das ist
   *  richtig so: die Namenssperre betrifft nur die Einstellungsseite. */
  nameLocked?: boolean;
}

/**
 * Schreibkörper für Anlegen/Ändern. `name` ist optional, weil Primärzeilen nie eine Bezeichnung
 * senden (D-01, D-19) — der Backend-Processor weist eine abweichende Primärbezeichnung mit 422 ab,
 * statt sie still zu verwerfen (33-03).
 */
export interface FuelAshCategoryPayload {
  name?: string;
  startValue: string | null;
  minValue: string | null;
  maxValue: string | null;
  binding: boolean;
}

/**
 * Der Sammelabruf (`getAll`) ist seit Plan 34-05 sowohl `ROLE_ADMIN` als auch
 * `ROLE_PLANT_EMPLOYEE` offen — Melder brauchen ihn für die gruppierte Brennstofftabelle
 * (zwölf Kategorien, `startValue`). Einzelabruf und **alle** Schreiboperationen
 * (`create`/`update`/`delete`) bleiben ausschließlich `ROLE_ADMIN` (T-34-01c: jede zusätzlich
 * geöffnete Operation wäre Fläche ohne Zweck). Die Reihenfolge kommt bereits sortiert vom Server
 * (D-11/D-13, `FuelAshCategoryCollectionProvider`) — dieser Client sortiert nicht nach.
 */
export const BASE_PATH = '/api/fuel_ash_categories';

export const fuelAshCategoriesApi = {
  getAll: async (): Promise<FuelAshCategory[]> => {
    const response = await api.get<CollectionResponse<FuelAshCategory>>(BASE_PATH);
    return normalizeCollection(response.data)['hydra:member'];
  },

  /**
   * Sekundär-Kategorien für das Meldeformular (Plan 34-11): filtert `getAll()` auf
   * `type === 'secondary'`, sortiert NICHT nach — die Reihenfolge (deutsche Sortierung,
   * Auffangkategorie zuletzt) kommt bereits vom Server, ein zweiter Sortierschritt hier wäre der
   * Rückschritt hinter `FuelAshCategoryCollectionProvider`.
   */
  getSecondaryForForm: async (): Promise<FuelAshCategory[]> => {
    const all = await fuelAshCategoriesApi.getAll();
    return all.filter((category) => category.type === 'secondary');
  },

  create: async (data: FuelAshCategoryPayload): Promise<FuelAshCategory> => {
    const response = await api.post<FuelAshCategory>(BASE_PATH, data);
    return response.data;
  },

  update: async (id: number, data: Partial<FuelAshCategoryPayload>): Promise<FuelAshCategory> => {
    const response = await api.patch<FuelAshCategory>(`${BASE_PATH}/${id}`, data, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`${BASE_PATH}/${id}`);
  },
};
